/**
 * Local games that survive the tab. [P6-resume]
 *
 * A local game used to exist only in the page's memory, so a crash, a stray refresh or a closed tab
 * destroyed it outright — Tony lost a game he was winning 20-12 to a freeze, and a bug reported
 * against a local game could never be replayed because nothing about it was written down.
 *
 * Nothing new has to be captured to fix that. The engine is deterministic and total, so a game IS
 * `(seed, options, actions[])`: the seed fixes the shuffle *and* how the deck splits across draw
 * stacks, and the action log holds every draw, placement and meeple in order. Replaying that through
 * the same pure engine reconstructs the game exactly — which is precisely what the server already
 * does for online rooms. This is that mechanism, pointed at localStorage.
 *
 * Deliberately NOT stored: `GameState`. It contains Maps and the union-find forest, it cannot survive
 * `JSON.stringify` intact, and storing it would create a second source of truth that could disagree
 * with the log. The log is the truth; the state is derived.
 */

import type { GameAction } from '@carc/engine';
import type { SeatSetup } from './session.js';

const STORAGE_KEY = 'carcassonne3d.savedGames.v1';
const MAX_SAVES = 12;

export interface SavedGame {
  readonly id: string;
  /** What the player calls it. They pick this, so they can find it again. */
  readonly name: string;
  readonly seed: number;
  readonly seats: readonly SeatSetup[];
  readonly options: { readonly stackCount: number; readonly turnSeconds: number; readonly packs: readonly string[] };
  readonly actions: readonly GameAction[];
  readonly savedAt: number;
  /** For the lobby list — cheap to store, and saves replaying every save just to describe it. */
  readonly tilesPlaced: number;
  readonly scores: readonly number[];
  readonly finished: boolean;
}

/**
 * Read the shelf.
 *
 * Total by construction: a browser can refuse localStorage entirely (private windows, blocked
 * cookies), and a half-written or older entry must never take the lobby down with it. Anything
 * unreadable is treated as "no saves" rather than thrown.
 */
export function loadSavedGames(): SavedGame[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isSavedGame).sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

function isSavedGame(value: unknown): value is SavedGame {
  if (typeof value !== 'object' || value === null) return false;
  const g = value as Partial<SavedGame>;
  return (
    typeof g.id === 'string' &&
    typeof g.seed === 'number' &&
    Array.isArray(g.actions) &&
    Array.isArray(g.seats) &&
    typeof g.options === 'object' &&
    g.options !== null
  );
}

/** Write the shelf back, newest first, oldest beyond `MAX_SAVES` dropped. */
function write(games: readonly SavedGame[]): void {
  try {
    const trimmed = [...games].sort((a, b) => b.savedAt - a.savedAt).slice(0, MAX_SAVES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // A full or unavailable store must never break play. The game continues unsaved.
  }
}

/** Insert or replace by id. Called after every committed action, so it must stay cheap. */
export function saveGame(game: SavedGame): void {
  const others = loadSavedGames().filter((g) => g.id !== game.id);
  write([game, ...others]);
}

export function deleteSavedGame(id: string): void {
  write(loadSavedGames().filter((g) => g.id !== id));
}

/** A short, human-typable id — the handle a player uses to tell one saved game from another. */
export function newGameId(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no look-alikes, same rule as room codes
  let out = '';
  for (let i = 0; i < 6; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

/** "2 hours ago" — the lobby needs to say which game is which, and time is how people remember. */
export function describeAge(savedAt: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - savedAt) / 1000));
  if (seconds < 90) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
